import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';

/**
 * App-wide confirmation dialog. Wrap the app in <ConfirmProvider>, then call
 * `const confirm = useConfirm()` and `if (await confirm({...})) doIt()`. Used
 * for deletes and any action that brings a significant change to a user or the
 * platform — replaces native window.confirm with a design-system dialog.
 */

export interface ConfirmOptions {
  title: string;
  message?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Red destructive styling on the confirm button (default false). */
  danger?: boolean;
}

type ConfirmFn = (opts: ConfirmOptions) => Promise<boolean>;

const ConfirmCtx = createContext<ConfirmFn>(async () => false);

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<{ opts: ConfirmOptions; resolve: (v: boolean) => void } | null>(null);

  const confirm = useCallback<ConfirmFn>(
    (opts) => new Promise<boolean>((resolve) => setState({ opts, resolve })),
    [],
  );

  const finish = (v: boolean) => {
    state?.resolve(v);
    setState(null);
  };

  return (
    <ConfirmCtx.Provider value={confirm}>
      {children}
      {state && (
        <ConfirmDialog opts={state.opts} onConfirm={() => finish(true)} onCancel={() => finish(false)} />
      )}
    </ConfirmCtx.Provider>
  );
}

export function useConfirm(): ConfirmFn {
  return useContext(ConfirmCtx);
}

function ConfirmDialog({
  opts,
  onConfirm,
  onCancel,
}: {
  opts: ConfirmOptions;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const danger = opts.danger ?? false;
  return (
    <div className="scrim" onClick={onCancel}>
      <div className="dialog" style={{ width: 'min(420px, 94vw)' }} onClick={(e) => e.stopPropagation()}>
        <div className="db" style={{ paddingTop: 22 }}>
          <div style={{ display: 'flex', gap: 13, alignItems: 'flex-start' }}>
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 9,
                display: 'grid',
                placeItems: 'center',
                flex: 'none',
                fontWeight: 700,
                fontSize: 18,
                fontFamily: 'var(--disp)',
                background: danger ? '#f3e0db' : 'var(--hairline)',
                color: danger ? 'var(--bad)' : 'var(--ink-soft)',
              }}
            >
              !
            </div>
            <div>
              <h3 style={{ fontFamily: 'var(--disp)', fontWeight: 500, fontSize: 18, marginBottom: 4 }}>
                {opts.title}
              </h3>
              {opts.message && (
                <p style={{ fontSize: 13, color: 'var(--ink-soft)', lineHeight: 1.5 }}>{opts.message}</p>
              )}
            </div>
          </div>
        </div>
        <div className="df">
          <button type="button" className="btn ghost" onClick={onCancel}>
            {opts.cancelLabel ?? 'Cancel'}
          </button>
          <button type="button" className={'btn ' + (danger ? 'danger' : 'pri')} onClick={onConfirm} autoFocus>
            {opts.confirmLabel ?? 'Confirm'}
          </button>
        </div>
      </div>
    </div>
  );
}
